import React from "react";
import { createPortal } from "react-dom";
import { useRoutePortalTarget } from "./layout/RoutePortalHost";

type QuickWindowAccent = "aqua" | "gold" | "violet" | "green";
type QuickWindowPlacement = "right" | "left" | "up-right" | "up-left";

type Props = {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  isDarkMode: boolean;
  onClose: () => void;
  onOpenFull?: () => void;
  openFullLabel?: string;
  accent?: QuickWindowAccent;
  anchorId?: string;
  placement?: QuickWindowPlacement;
  anchorOffsetY?: number;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
};

type OverlayBounds = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const accentTokens = (accent: QuickWindowAccent, dark: boolean) => {
  const map = {
    aqua: dark
      ? { main: "#67D8E8", soft: "rgba(103,216,232,0.10)", border: "rgba(103,216,232,0.20)", text: "#B9F4FB" }
      : { main: "#2F80ED", soft: "rgba(47,128,237,0.08)", border: "rgba(47,128,237,0.16)", text: "#245FAD" },
    gold: dark
      ? { main: "#F0C86D", soft: "rgba(240,200,109,0.10)", border: "rgba(240,200,109,0.18)", text: "#F6D98E" }
      : { main: "#B78324", soft: "rgba(183,131,36,0.08)", border: "rgba(183,131,36,0.16)", text: "#8A611A" },
    violet: dark
      ? { main: "#AFA3F7", soft: "rgba(175,163,247,0.10)", border: "rgba(175,163,247,0.18)", text: "#DAD4FF" }
      : { main: "#6551B9", soft: "rgba(101,81,185,0.08)", border: "rgba(101,81,185,0.15)", text: "#5543A1" },
    green: dark
      ? { main: "#7BE0AE", soft: "rgba(123,224,174,0.09)", border: "rgba(123,224,174,0.18)", text: "#BFF5D8" }
      : { main: "#2F9E68", soft: "rgba(47,158,104,0.08)", border: "rgba(47,158,104,0.15)", text: "#277E55" },
  };
  return map[accent];
};

const rectsOverlap = (a: DOMRect, b: DOMRect) =>
  a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

const readOverlayBounds = (): OverlayBounds => {
  const shell = document.getElementById("roddo-app-shell");
  const mapRoot = document.querySelector<HTMLElement>(
    '[data-roddo-tour="big-blue-map"]',
  );
  const header =
    shell?.querySelector<HTMLElement>(".roddo-final-header") ??
    document.querySelector<HTMLElement>(".roddo-final-header");
  const nav =
    shell?.querySelector<HTMLElement>(".roddo-final-bottom-nav") ??
    document.querySelector<HTMLElement>(".roddo-final-bottom-nav");

  const shellRect = shell?.getBoundingClientRect();
  const mapRect = mapRoot?.getBoundingClientRect();
  const headerRect = header?.getBoundingClientRect();
  const navRect = nav?.getBoundingClientRect();

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Big Blue is the authoritative overlay surface. On desktop the navigation
  // becomes a LEFT rail, so using navRect.top as the bottom edge collapses the
  // available height to ~0. Using the real map rectangle works for both the
  // desktop workspace and the compact/mobile shell.
  const baseLeft = mapRect?.left ?? shellRect?.left ?? 0;
  const baseRight = mapRect?.right ?? shellRect?.right ?? viewportWidth;
  const baseTop =
    mapRect?.top ?? headerRect?.bottom ?? shellRect?.top ?? 0;
  const baseBottom = mapRect?.bottom ?? shellRect?.bottom ?? viewportHeight;

  let left = Math.max(0, Math.round(baseLeft));
  let right = Math.min(viewportWidth, Math.round(baseRight));
  let top = Math.max(0, Math.round(baseTop));
  let bottom = Math.min(viewportHeight, Math.round(baseBottom));

  if (navRect && mapRect && rectsOverlap(navRect, mapRect)) {
    const mapWidth = Math.max(1, mapRect.width);
    const mapHeight = Math.max(1, mapRect.height);

    // Compact/mobile: bottom navigation spans most of the map width.
    const isBottomDock =
      navRect.width >= mapWidth * 0.6 &&
      navRect.height <= Math.max(180, mapHeight * 0.38) &&
      navRect.top >= mapRect.top + mapHeight * 0.45;

    // Desktop: navigation is a tall, narrow rail. Normally the map workspace
    // already starts to its right, but clamp defensively if the rectangles
    // overlap during a resize frame.
    const isLeftRail =
      navRect.height >= mapHeight * 0.55 &&
      navRect.width <= Math.max(180, mapWidth * 0.35) &&
      navRect.left <= mapRect.left + Math.max(40, mapWidth * 0.12);

    if (isBottomDock) bottom = Math.min(bottom, Math.round(navRect.top));
    if (isLeftRail) left = Math.max(left, Math.round(navRect.right));
  }

  return {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
};

const BigBlueQuickWindow: React.FC<Props> = ({
  title,
  subtitle,
  icon,
  isDarkMode,
  onClose,
  onOpenFull,
  openFullLabel = "Open full",
  accent = "aqua",
  headerAction,
  anchorId,
  placement = "right",
  anchorOffsetY = 0,
  children,
}) => {
  const portalTarget = useRoutePortalTarget();
  const tone = accentTokens(accent, isDarkMode);
  const panelRef = React.useRef<HTMLElement | null>(null);
  const [bounds, setBounds] = React.useState<OverlayBounds | null>(null);
  const [desktopPos, setDesktopPos] = React.useState<{ top: number; left: number; maxHeight: number } | null>(null);

  const syncPosition = React.useCallback(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const nextBounds = readOverlayBounds();
    setBounds(nextBounds);

    const desktop = window.innerWidth >= 1024;
    const anchor = anchorId
      ? document.querySelector<HTMLElement>(`[data-roddo-quick-anchor="${anchorId}"]`)
      : null;

    if (!desktop || !anchor || nextBounds.width <= 0 || nextBounds.height <= 0) {
      setDesktopPos(null);
      return;
    }

    const gap = 12;
    const pad = 14;
    const anchorRect = anchor.getBoundingClientRect();
    const panelRect = panelRef.current?.getBoundingClientRect();
    const panelWidth = Math.min(390, Math.max(260, nextBounds.width - pad * 2));
    const maxHeight = Math.max(220, nextBounds.height - pad * 2);
    const panelHeight = Math.min(panelRect?.height || Math.min(520, maxHeight), maxHeight);

    let left = anchorRect.right + gap;
    let top = anchorRect.top + anchorOffsetY;

    if (placement === "left" || placement === "up-left") {
      left = anchorRect.left - gap - panelWidth;
    }

    if (placement === "up-left" || placement === "up-right") {
      top = anchorRect.bottom - panelHeight + anchorOffsetY;
    }

    const minLeft = nextBounds.left + pad;
    const maxLeft = nextBounds.left + nextBounds.width - panelWidth - pad;
    const minTop = nextBounds.top + pad;
    const maxTop = nextBounds.top + nextBounds.height - panelHeight - pad;

    setDesktopPos({
      left: Math.round(Math.max(minLeft, Math.min(maxLeft, left))),
      top: Math.round(Math.max(minTop, Math.min(maxTop, top))),
      maxHeight,
    });
  }, [anchorId, anchorOffsetY, placement]);

  React.useLayoutEffect(() => {
    syncPosition();
    const raf = window.requestAnimationFrame(syncPosition);
    window.addEventListener("resize", syncPosition);
    window.addEventListener("scroll", syncPosition, true);
    window.visualViewport?.addEventListener("resize", syncPosition);
    window.visualViewport?.addEventListener("scroll", syncPosition);

    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(syncPosition)
      : null;
    if (panelRef.current) resizeObserver?.observe(panelRef.current);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", syncPosition);
      window.removeEventListener("scroll", syncPosition, true);
      window.visualViewport?.removeEventListener("resize", syncPosition);
      window.visualViewport?.removeEventListener("scroll", syncPosition);
      resizeObserver?.disconnect();
    };
  }, [syncPosition]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  React.useEffect(() => {
    if (!desktopPos || !anchorId) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && panelRef.current?.contains(target)) return;

      // Some controls inside a quick window intentionally render through a
      // document.body portal (for example Crew DM message actions). Treat those
      // portals as owned by the quick window so clicking Edit/Remove does not
      // dismiss the parent panel before the action can run.
      const targetElement =
        target instanceof Element
          ? target
          : ((target as any)?.parentElement as Element | null | undefined);
      if (targetElement?.closest?.('[data-roddo-quick-owned="true"]')) return;

      const anchor = document.querySelector<HTMLElement>(`[data-roddo-quick-anchor="${anchorId}"]`);
      if (target && anchor?.contains(target)) return;
      onClose();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [anchorId, desktopPos, onClose]);

  if (!portalTarget || !bounds || bounds.width <= 0 || bounds.height <= 0) {
    return null;
  }

  const panel = (
    <section
      ref={panelRef}
      role="dialog"
      aria-modal={desktopPos ? undefined : "true"}
      aria-label={title}
      onClick={(event) => event.stopPropagation()}
      className="pointer-events-auto relative flex min-h-0 w-[min(92vw,390px)] flex-col overflow-hidden rounded-[22px] border"
      style={{
        maxHeight: desktopPos ? `${desktopPos.maxHeight}px` : "calc(100% - 4px)",
        background: isDarkMode ? "#0A1221" : "#FBF8F2",
        borderColor: isDarkMode ? "rgba(132,151,190,0.16)" : "rgba(116,86,54,0.13)",
        boxShadow: isDarkMode
          ? "0 24px 56px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.035)"
          : "0 24px 56px rgba(69,49,29,0.18), inset 0 1px 0 rgba(255,255,255,0.84)",
      }}
    >
      <div className="shrink-0 pt-2.5" aria-hidden="true">
        <div
          className="mx-auto h-[3px] w-10 rounded-full"
          style={{ background: isDarkMode ? "rgba(255,255,255,0.18)" : "rgba(80,62,45,0.14)" }}
        />
      </div>

      <header className="relative flex min-h-[74px] shrink-0 items-center gap-3 px-4 pb-3 pt-2.5">
        <div
          className="absolute bottom-0 left-4 right-4 h-px"
          style={{ background: isDarkMode ? "rgba(132,151,190,0.09)" : "rgba(91,67,49,0.07)" }}
          aria-hidden="true"
        />
        {icon ? (
          <div
            className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] border text-[20px]"
            style={{ background: tone.soft, borderColor: tone.border, color: tone.text }}
          >
            {icon}
          </div>
        ) : null}

        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[18px] font-black leading-[1.1]" style={{ color: isDarkMode ? "#F4F7FB" : "#283540" }}>
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-1 line-clamp-1 text-[10px] font-bold leading-4" style={{ color: isDarkMode ? "#8F9DB0" : "#786D62" }}>
              {subtitle}
            </p>
          ) : null}
        </div>

        {headerAction ? <div className="shrink-0">{headerAction}</div> : null}

        <button
          type="button"
          onClick={onClose}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full border text-[16px] font-black transition hover:-translate-y-px active:translate-y-0 active:scale-95"
          style={{
            background: isDarkMode ? "#101A2A" : "#FFFFFF",
            borderColor: isDarkMode ? "rgba(132,151,190,0.13)" : "rgba(91,67,49,0.09)",
            color: isDarkMode ? "#DCE4EF" : "#62584F",
            boxShadow: isDarkMode ? "0 4px 12px rgba(0,0,0,0.14)" : "0 4px 12px rgba(89,65,37,0.06)",
          }}
          aria-label={`Close ${title}`}
        >
          ×
        </button>
      </header>

      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3.5"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {children}
      </div>

      {onOpenFull ? (
        <footer
          className="shrink-0 border-t px-4 pb-4 pt-3"
          style={{
            borderColor: isDarkMode ? "rgba(132,151,190,0.09)" : "rgba(91,67,49,0.07)",
            background: isDarkMode ? "#0A1221" : "#FBF8F2",
          }}
        >
          <div className="flex justify-center">
            <button
              type="button"
              onClick={onOpenFull}
              className="inline-flex h-10 max-w-full items-center justify-center rounded-[13px] border px-4 text-[11px] font-black transition hover:-translate-y-px active:translate-y-0 active:scale-[0.99]"
              style={{
                minWidth: "168px",
                background: isDarkMode ? "#F0C86D" : "#2F80ED",
                borderColor: isDarkMode ? "rgba(240,200,109,0.30)" : "rgba(47,128,237,0.26)",
                color: isDarkMode ? "#1A2233" : "#FFFFFF",
                boxShadow: isDarkMode
                  ? "0 6px 14px rgba(240,200,109,0.16)"
                  : "0 6px 14px rgba(47,128,237,0.14)",
              }}
            >
              <span>{openFullLabel}</span>
            </button>
          </div>
        </footer>
      ) : null}
    </section>
  );

  if (desktopPos) {
    return createPortal(
      <div
        className="fixed z-[6600] pointer-events-none"
        style={{ top: desktopPos.top, left: desktopPos.left }}
      >
        {panel}
      </div>,
      portalTarget,
    );
  }

  return createPortal(
    <div
      className="fixed z-[6600] flex items-center justify-center pointer-events-auto"
      style={{
        top: bounds.top,
        left: bounds.left,
        width: bounds.width,
        height: bounds.height,
        background: isDarkMode ? "rgba(2,8,18,0.42)" : "rgba(34,27,20,0.22)",
        padding: "14px",
      }}
      onClick={onClose}
      role="presentation"
    >
      {panel}
    </div>,
    portalTarget,
  );

};

export default BigBlueQuickWindow;
